/**
 * SavedProducts — Session 4B. Newest first; reopen without rescanning;
 * one-tap re-log; compare any two/three. Guests: localStorage until signup.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { useGuestStore } from "../../store/guestStore";
import { fireScanEvent } from "../../utils/compareFacts";
import CompareView from "./CompareView";
import LabelResults from "./LabelResults";

const T = {
  base: "#ECEAE6", ink: "#2A2825", ink2: "rgba(42,40,37,0.65)",
  ink3: "rgba(42,40,37,0.40)", line: "rgba(42,40,37,0.10)",
};
const HEAD = { fontFamily: "'Barlow Condensed', system-ui, sans-serif" };
const BODY = { fontFamily: "'Barlow', system-ui, sans-serif" };

const asLabel = (canonical) => ({
  canonical,
  confidence: (canonical?.confidence?.overall ?? 0) >= 0.85 ? "high" : "medium",
  ingredients_truncated: false,
});

export default function SavedProducts() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const guestStore = useGuestStore();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null); // canonical
  const [selected, setSelected] = useState([]); // product_ids
  const [comparing, setComparing] = useState(false);

  const load = async () => {
    if (user) {
      try {
        const { data } = await apiClient.get("/nutrition/saved");
        setRows(data.products || []);
      } catch {
        setRows([]);
      }
    } else {
      setRows(guestStore.savedProducts || []);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const compareProducts = useMemo(
    () => (rows || []).filter((r) => selected.includes(r.product_id)).map((r) => r.canonical),
    [rows, selected]
  );

  const toggle = (pid) =>
    setSelected((s) => (s.includes(pid) ? s.filter((x) => x !== pid)
      : s.length >= 3 ? s : [...s, pid]));

  const relog = async (r) => {
    if (!user) {
      toast.error("Sign up to log products to your day");
      return;
    }
    const serving = r.canonical?.serving || {};
    const mode = serving.package_size_value != null && serving.package_size_value < 100
      ? "whole_pack" : serving.size_value != null ? "serving"
      : serving.package_size_value != null ? "whole_pack" : null;
    if (!mode) {
      setOpen(r.canonical);
      return;
    }
    try {
      const { data } = await apiClient.post("/nutrition/log-label", { canonical: r.canonical, mode });
      toast.success(`Logged · ${data?.logged?.calories ?? ""} kcal`);
      fireScanEvent("log_pressed", { from: "saved" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log");
    }
  };

  const remove = async (r) => {
    if (user) {
      await apiClient.delete(`/nutrition/saved/${r.product_id}`).catch(() => {});
    } else {
      guestStore.removeSavedProduct(r.product_id);
    }
    setSelected((s) => s.filter((x) => x !== r.product_id));
    load();
  };

  if (comparing && compareProducts.length >= 2) {
    return (
      <div style={{ ...BODY, minHeight: "100vh", background: T.base, padding: 16, maxWidth: 672, margin: "0 auto" }}>
        <CompareView products={compareProducts} onClose={() => setComparing(false)} />
      </div>
    );
  }

  if (open) {
    return (
      <div style={{ ...BODY, minHeight: "100vh", background: T.base, padding: 16, maxWidth: 672, margin: "0 auto" }}>
        <LabelResults label={asLabel(open)} onDiscard={() => setOpen(null)} onSaved={() => setOpen(null)} />
      </div>
    );
  }

  return (
    <div data-testid="saved-products-page"
         style={{ ...BODY, minHeight: "100vh", background: T.base, color: T.ink, padding: 16, maxWidth: 672, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button type="button" onClick={() => navigate(-1)} data-testid="saved-back"
                style={{ background: "transparent", color: T.ink }}>
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ ...HEAD, fontSize: 28, fontWeight: 800 }}>Saved products</h1>
      </div>

      {selected.length >= 2 && (
        <button type="button" data-testid="saved-compare"
                onClick={() => { fireScanEvent("compare_started", { from: "saved" }); setComparing(true); }}
                style={{ ...BODY, marginTop: 12, width: "100%", height: 44, borderRadius: 100,
                         background: T.ink, color: T.base, fontWeight: 600, fontSize: 14 }}>
          Compare {selected.length} products
        </button>
      )}

      {rows === null ? (
        <div style={{ marginTop: 24, color: T.ink2, fontSize: 14 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ marginTop: 24, color: T.ink2, fontSize: 14 }} data-testid="saved-empty">
          Nothing saved yet. Scan a packet and tap Save to keep it here.
        </div>
      ) : (
        <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
          {rows.map((r) => {
            const p = r.canonical?.product || {};
            const kcal = r.canonical?.nutrition_per_100?.energy_kcal;
            return (
              <div key={r.product_id} data-testid={`saved-card-${r.product_id}`}
                   style={{ border: `1px solid ${T.line}`, borderRadius: 12, padding: 14,
                            display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" checked={selected.includes(r.product_id)}
                       onChange={() => toggle(r.product_id)}
                       data-testid={`saved-select-${r.product_id}`}
                       style={{ width: 18, height: 18, accentColor: T.ink }} />
                <button type="button" onClick={() => setOpen(r.canonical)}
                        style={{ flex: 1, textAlign: "left", background: "transparent", color: T.ink }}
                        data-testid={`saved-open-${r.product_id}`}>
                  <div style={{ ...HEAD, fontSize: 17, fontWeight: 700 }}>
                    {p.name || p.brand || "Product"}
                  </div>
                  <div style={{ fontSize: 12, color: T.ink2 }}>
                    {[p.brand && p.name ? p.brand : null,
                      kcal != null ? `${Math.floor(kcal + 0.5)} kcal / 100g` : null,
                      new Date(r.saved_at).toLocaleDateString()].filter(Boolean).join(" · ")}
                  </div>
                </button>
                <button type="button" onClick={() => relog(r)} data-testid={`saved-relog-${r.product_id}`}
                        style={{ ...BODY, height: 34, padding: "0 12px", borderRadius: 100, fontSize: 12,
                                 fontWeight: 700, background: T.ink, color: T.base }}>
                  Log
                </button>
                <button type="button" onClick={() => remove(r)} data-testid={`saved-delete-${r.product_id}`}
                        style={{ background: "transparent", color: T.ink3 }}>
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
